import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Shield, Lock, CreditCard } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import PaymentForm, { useSubscriptionPlans, type SubscriptionPlan } from "@/components/PaymentForm";
import { Skeleton } from "@/components/ui/skeleton";

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriptionPlans, isLoading: isLoadingPlans } = useSubscriptionPlans();
  
  // Get plan from navigation state or default to Basic (id: 1)
  const navPlan = (location.state as { plan?: { name: string } } | undefined)?.plan;
  const defaultPlanId = navPlan && subscriptionPlans.length > 0
    ? subscriptionPlans.find(p => p.name.toLowerCase() === navPlan.name.toLowerCase())?.id.toString() || "1"
    : "1";
  
  const [selectedPlanId, setSelectedPlanId] = useState<string>(defaultPlanId);
  
  const selectedPlan = subscriptionPlans.find(
    (plan) => plan.id.toString() === selectedPlanId
  ) || subscriptionPlans[0]; // Fallback to first plan if not found
  
  // Calculate pricing
  const subtotal = selectedPlan?.price || 0;
  const tax = Math.round(subtotal * 0.18 * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;

  // Show loading state while fetching plans
  if (isLoadingPlans) {
    return (
      <Layout>
        <div className="min-h-screen bg-background">
          <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
            <div className="max-w-4xl mx-auto">
              <Skeleton className="h-12 w-48 mb-6" />
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
                <div className="lg:col-span-3">
                  <Skeleton className="h-96 w-full" />
                </div>
                <div className="lg:col-span-2">
                  <Skeleton className="h-96 w-full" />
                </div>
              </div>
            </div>
          </main>
        </div>
      </Layout>
    );
  }

  // Show error state if plans failed to load
  if (!subscriptionPlans || subscriptionPlans.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen bg-background">
          <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-sm text-destructive mb-4">Failed to load subscription plans</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </main>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/my-account")}
            className="mb-4 sm:mb-6 text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            ← Back to Account
          </Button>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
              <div className="lg:col-span-3 animate-fade-in">
                <div className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-card border border-border">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20 flex-shrink-0">
                      <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h1 className="text-xl sm:text-2xl font-bold text-foreground">Payment Details</h1>
                      <p className="text-sm sm:text-base text-muted-foreground">Select your subscription plan</p>
                    </div>
                  </div>

                  <PaymentForm selectedPlanId={selectedPlanId} onPlanChange={setSelectedPlanId} />
                </div>
              </div>

              <div className="lg:col-span-2 animate-slide-up" style={{ animationDelay: "0.1s" }}>
                <div className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-card border border-border lg:sticky lg:top-24">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Order Summary</h2>

                  {selectedPlan && (
                    <>
                      <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-muted/50 mb-4 sm:mb-6">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm sm:text-base font-medium text-foreground">{selectedPlan.name} Plan</h3>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              Yearly subscription + GST
                            </p>
                          </div>
                          <span className="text-base sm:text-lg font-semibold text-foreground whitespace-nowrap">
                            ₹{selectedPlan.price.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                        <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                          <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                          <span>30-day money back guarantee</span>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                          <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent flex-shrink-0" />
                          <span>Secure SSL encryption</span>
                        </div>
                      </div>

                      <div className="pt-3 sm:pt-4 border-t border-border">
                        <h3 className="text-sm sm:text-base font-medium text-foreground mb-2 sm:mb-3">What's included:</h3>
                        <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
                          {selectedPlan.features.map((item) => (
                            <li key={item} className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default Payment;

